import type { RelayManagedEndpoint } from "@maito/contracts/relay";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";

import { ConnectionBlockedError, type PreparedConnection } from "../connection/model.ts";
import { environmentEndpointUrl } from "../environment/endpoint.ts";
import { CloudSession } from "../platform/capabilities.ts";
import { executeEnvironmentHttpRequest, makeEnvironmentHttpApiClient } from "../rpc/http.ts";
import {
  buildEnvironmentAuthHeaders,
  withEnvironmentCredentials,
} from "../state/environmentHttpAuth.ts";
import { ManagedRelayClient, type ManagedRelayDpopSigner } from "./managedRelay.ts";

const REQUEST_TIMEOUT_MS = 15_000;
const MANAGED_ENDPOINT_PROVIDER_KIND = "cloudflare_tunnel" as const;

function requestOrigin(httpBaseUrl: string) {
  const url = new URL(httpBaseUrl);
  return {
    localHttpHost: "127.0.0.1",
    localHttpPort: Number(url.port || (url.protocol === "https:" ? 443 : 80)),
  };
}

function ensureLinkedEnvironment(input: {
  readonly prepared: PreparedConnection;
  readonly environmentId: string;
  readonly endpoint: RelayManagedEndpoint;
}) {
  if (input.environmentId !== input.prepared.environmentId) {
    return Effect.fail(
      new ConnectionBlockedError({
        reason: "configuration",
        detail: "Maito Connect linked a different environment.",
      }),
    );
  }
  if (input.endpoint.providerKind !== MANAGED_ENDPOINT_PROVIDER_KIND) {
    return Effect.fail(
      new ConnectionBlockedError({
        reason: "configuration",
        detail: "Maito Connect did not create a secure endpoint.",
      }),
    );
  }
  return Effect.succeed(input.endpoint);
}

/** Link the connected environment to its signed-in Maito account and return its public endpoint. */
export const ensureManagedEnvironmentEndpoint = Effect.fn(
  "clientRuntime.relay.ensureManagedEnvironmentEndpoint",
)(function* (input: {
  readonly prepared: PreparedConnection;
  readonly signer: Option.Option<ManagedRelayDpopSigner["Service"]>;
}) {
  const relay = yield* ManagedRelayClient;
  const cloudSession = yield* CloudSession;
  const clerkToken = yield* cloudSession.clerkToken;

  const existing = (yield* relay.listEnvironments({ clerkToken })).find(
    (environment) => environment.environmentId === input.prepared.environmentId,
  );
  if (existing?.endpoint.providerKind === MANAGED_ENDPOINT_PROVIDER_KIND) {
    return existing.endpoint;
  }

  const challenge = yield* relay.createEnvironmentLinkChallenge({
    clerkToken,
    payload: {
      notificationsEnabled: false,
      liveActivitiesEnabled: false,
      managedTunnelsEnabled: true,
    },
  });
  const client = yield* makeEnvironmentHttpApiClient(input.prepared.httpBaseUrl);
  const linkProofUrl = environmentEndpointUrl(
    input.prepared.httpBaseUrl,
    "/api/connect/link-proof",
  );
  const linkProofHeaders = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "POST",
    linkProofUrl,
    input.signer,
  );
  const proof = yield* executeEnvironmentHttpRequest(
    linkProofUrl,
    REQUEST_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.connect.linkProof({
        headers: linkProofHeaders,
        payload: {
          challenge: challenge.challenge,
          relayIssuer: relay.relayUrl,
          endpoint: {
            httpBaseUrl: input.prepared.httpBaseUrl,
            wsBaseUrl: input.prepared.socketUrl,
            providerKind: MANAGED_ENDPOINT_PROVIDER_KIND,
          },
          // New servers replace this client-visible address with their actual
          // loopback listener. Keeping it valid preserves compatibility with
          // older directly connected servers.
          origin: requestOrigin(input.prepared.httpBaseUrl),
        },
      }),
    ),
  );
  const link = yield* relay.linkEnvironment({
    clerkToken,
    payload: {
      proof,
      notificationsEnabled: false,
      liveActivitiesEnabled: false,
      managedTunnelsEnabled: true,
    },
  });
  const endpoint = yield* ensureLinkedEnvironment({
    prepared: input.prepared,
    environmentId: link.environmentId,
    endpoint: link.endpoint,
  });

  const relayConfigUrl = environmentEndpointUrl(
    input.prepared.httpBaseUrl,
    "/api/connect/relay-config",
  );
  const relayConfigHeaders = yield* buildEnvironmentAuthHeaders(
    input.prepared.httpAuthorization,
    "POST",
    relayConfigUrl,
    input.signer,
  );
  yield* executeEnvironmentHttpRequest(
    relayConfigUrl,
    REQUEST_TIMEOUT_MS,
    withEnvironmentCredentials(
      input.prepared.httpAuthorization,
      client.connect.relayConfig({
        headers: relayConfigHeaders,
        payload: {
          relayUrl: relay.relayUrl,
          relayIssuer: link.relayIssuer,
          cloudUserId: link.cloudUserId,
          environmentCredential: link.environmentCredential,
          cloudMintPublicKey: link.cloudMintPublicKey,
          endpoint: link.endpoint,
          endpointRuntime: link.endpointRuntime,
        },
      }),
    ),
  );

  return endpoint;
});
